// src/hooks/useFeatureAccess.js
//
// SINGLE SOURCE OF TRUTH for feature visibility and access.
//
// Before this hook the same gates were derived twice - once in App.jsx's
// FeatureRoute/AddonRoute guards and once in Layout.jsx's sidebar filter -
// and the two had drifted: the sidebar enforced subscription tier and a CHW
// carve-out that the routes did not, so a Lite user or a CHW who typed a Pro
// URL reached a view the rail deliberately hid from them.
//
// Both consumers now read these predicates, so the rail and the router can
// no longer disagree by construction.
//
// Layering (unchanged semantics, just centralized):
//   Tier   - Pro/Command only. Survives a downgrade that left a stale add-on
//            or pref behind, which is why it is checked FIRST and separately.
//   Prefs  - the practice turned the feature off in the onboarding wizard.
//   Add-on - the SKU is active (RLS-scoped to the caller's practice).
//   Role   - CHWs keep HRSN (they run SDOH screenings) but not the rest of
//            the Pro AI tooling.
//
// `ready` is false while prefs or add-ons are still loading. Consumers MUST
// wait on it: acting early makes every gate look false, which would redirect
// a legitimate user off a page they own.

import { useMemo } from "react";
import { useAuth } from "../auth/AuthProvider";
import {
  usePracticePreferences,
  isAiFeatureEnabled,
  isHrsnEnabled,
  getCmDeliveryModel,
} from "./usePracticePreferences";
import { useActiveAddons } from "./useActiveAddons";
import { canReadChartPrep } from "../lib/chartPrepCore";

// Tiers that include the Pro feature set. Command is a superset of Pro.
const PRO_TIERS = ["Pro", "Command"];

export function useFeatureAccess() {
  const { role, tier } = useAuth();
  const { prefs, loaded } = usePracticePreferences();
  const { has: hasAddon, loading: addonsLoading } = useActiveAddons();

  return useMemo(() => {
    const ready = loaded && !addonsLoading;

    const isProTier = PRO_TIERS.includes(tier);
    const isCHW = role === "CHW";
    const cmDeliveryModel = getCmDeliveryModel(prefs);
    const deliversCm = cmDeliveryModel !== "None";

    // Each predicate returns the full access answer for one feature, so a
    // caller can never accidentally apply only half of the gate stack.
    const can = {
      // Pro - AI tooling. CHWs are excluded from all of it except HRSN.
      // Chart Prep also needs a role that can read the notes (RLS:
      // chart_prep_can_see). Billing, Pharmacist and the CM roles would
      // otherwise get a nav item and a chart tab that are always empty.
      chartPrep:    () => isProTier && !isCHW && canReadChartPrep(role) && isAiFeatureEnabled(prefs, "chart_prep"),
      aiAssistant:  () => isProTier && !isCHW && isAiFeatureEnabled(prefs, "ai_assistant"),
      outreach:     () => isProTier && !isCHW && isAiFeatureEnabled(prefs, "outreach_drafts"),
      // Inbound SMS (texts, keyword sorting, automatic replies, on-call
      // paging, morning queue) works on every tier since the after-hours
      // triage release (Oct 2026). AI drafts stay Pro / Command.
      inboundSms:   () => !isCHW,
      inboundAiDrafts: () => isProTier && !isCHW && isAiFeatureEnabled(prefs, "inbound_drafts"),

      // HRSN - CHWs DO keep this one; they run the SDOH screenings.
      hrsn:         () => isProTier && isHrsnEnabled(prefs),

      // Care Management - Pro/Command capability, gated on delivery model.
      careManagement: () => isProTier && deliversCm,

      // CM Co-pilot - care management PLUS both add-on SKUs.
      cmCopilot: () =>
        isProTier &&
        !isCHW &&
        deliversCm &&
        hasAddon("care_management_bundle") &&
        hasAddon("amh_cm_copilot_addon"),
    };

    return { ready, can, isProTier, isCHW, cmDeliveryModel, deliversCm, prefs };
  }, [role, tier, prefs, loaded, addonsLoading, hasAddon]);
}
